import {Separator, showNotification} from 'platform/components';
import {Box, HStack, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback} from 'react';

import {head, isNil, mergeAll} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {
  PostWarehouseArticleItemRequest,
  PostWarehouseArticleItemResponse,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {
  ArticleFolderTree,
  DmsDataGrid,
  catchUnhandledDataGridActions,
  useWarehouseDataGridTreeFolder,
} from '@dms/shared';

import {Nullish, TestIdProps, suffixTestId, useLatestValue} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {WarehouseArticlesTabExternalFilters} from './WarehouseArticlesTabExternalFilters';

interface WarehouseArticlesTabProps extends TestIdProps {
  serviceOrderIssueNoteId: string | Nullish;
  serviceOrderId: string | Nullish;
  serviceOrderVariantId: string | Nullish;
  onAddItemToBasket: (
    arg: PostWarehouseArticleItemRequest
  ) => Promise<PostWarehouseArticleItemResponse | void>;
  hasPendingRequest: boolean;
}

const WAREHOUSE_ARTICLES_EXTERNAL_FILTER_ID = 'wharticles-external-filter-id';

export function WarehouseArticlesTab(props: WarehouseArticlesTabProps) {
  const [folderTreeRef, dataGridModifier] = useWarehouseDataGridTreeFolder();

  const hasPendingRequestRef = useLatestValue(props.hasPendingRequest);

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => {
      const modifiedFilter = mergeAll([filter, {serviceOrderId: props.serviceOrderId}]);

      return dataGridModifier ? dataGridModifier?.queryModifier(modifiedFilter) : modifiedFilter;
    },
    [props.serviceOrderId, dataGridModifier]
  );

  const actionCallback: ActionCallback = async ({actionKey, rowId, refreshData}) => {
    if (hasPendingRequestRef.current) {
      return showNotification.warning(
        i18n.t('entity.warehouse.labels.thereIsAnotherOngoingAction')
      );
    }

    const id = isArray(rowId) ? head(rowId) : rowId;

    if (isNil(id)) {
      throw Error('Row id must be specified');
    }

    await match(actionKey)
      .with('dispatch', async () => {
        if (props.serviceOrderIssueNoteId) {
          await props
            .onAddItemToBasket({
              serviceOrderIssueNoteId: props.serviceOrderIssueNoteId,
              body: {articleId: id},
            })
            .then(() => refreshData());
        }
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <HStack height="100%">
      <Box flex={1}>
        <VStack height="100%">
          <Box>
            <div id={WAREHOUSE_ARTICLES_EXTERNAL_FILTER_ID}></div>
          </Box>
          <Separator orientation="horizontal" spacing={4} />
          <ArticleFolderTree
            ref={folderTreeRef}
            data-testid={suffixTestId('articleFolderTree', props)}
          />
        </VStack>
      </Box>
      <Separator orientation="vertical" />
      <Box flex={3}>
        <DmsDataGrid
          // DG must be re-rendered on folder change to update the query modifier

          key={dataGridModifier?.key}
          gridCode="issue-note-service-order-warehouse-articles"
          externalFilterId={WAREHOUSE_ARTICLES_EXTERNAL_FILTER_ID}
          _useAsLastResort_definitionBehaviorOverrides={{filterMode: 'NATIVE'}}
          filterComponent={(datagridProps) => (
            <WarehouseArticlesTabExternalFilters
              serviceOrderVariantId={props.serviceOrderVariantId}
              dataGridKey={dataGridModifier?.key}
              {...datagridProps}
            />
          )}
          actionCallback={actionCallback}
          queryModifier={queryModifier}
          data-testid="issueNoteServiceOrderWarehouseArticles"
        />
      </Box>
    </HStack>
  );
}
